import { type CSSProperties, type Dispatch, type SetStateAction, useCallback, useEffect, useMemo, useRef, useState } from "react";
import ClockIcon from "../../image/icons/map/details/ClockIcon.svg?react";
import LeafIcon from "../../image/icons/status/LeafIcon.svg";
import { ItineraryService } from "../../services/ItineraryService";
import { appendRouteHistory, navigationSessionSlice, routeHistorySlice, useEphemeralSelector } from "../../store/ephemeral";
import { getUIColorPalette } from "../../theme/ColorPalette";
import { getUserLocation, subscribeToLocation } from "../../tools/GpsLocationAdapter";
import { formatTime } from "../../tools/TimeRelated";
import { type Route, type UserChoice } from "../../types/Forms";
import { type DepartureAndArrival, type Location } from "../../types/geocode-ui.types";
import { type RouteHistory } from "../../types/GlobalContext";
import { NavigationMode } from "../../types/Navigation";
import {
    buildItineraryListCardStyle,
    ITINERARY_LIST_CARD_CHROME_CLASSNAME,
} from "../ItinerariesPlanner/itineraryList/itineraryListCard.styles";

const itineraryService = new ItineraryService();

interface CarItineraryButtonProps {
    "type":                       "autoroute_peages" | "no_autoroute_peages" | "eco_friendly";
    "selectedInputs":             DepartureAndArrival;
    "userChoice":                 UserChoice;
    "updateUserChoice":           ( updates: Partial<UserChoice> ) => void;
    "isReturn"?:                  boolean;
    "setSelectedCarRouteId":      Dispatch<SetStateAction<string>>;
    "selectedCarRouteId":         string;
    "itineraries":                Route[];
    "setItineraries":             Dispatch<SetStateAction<Route[]>>;
    "setGoItineraries":           Dispatch<SetStateAction<Route[]>>;
    "setBackupReturnItineraries": Dispatch<SetStateAction<Route[]>>;

    /** Whether dark overlay palette tokens should be used. */
    "isDarkUi"?: boolean;
}

const CarItineraryButton: React.FC<CarItineraryButtonProps> = ( {
    type,
    selectedInputs,
    userChoice,
    updateUserChoice,
    isReturn = false,

    // selectedCarRouteId, // Unused - reserved for future feature
    setSelectedCarRouteId,

    // itineraries, // Unused - reserved for future feature
    setItineraries,
    setGoItineraries,
    setBackupReturnItineraries,
    isDarkUi = false,
} ) => {
    const palette = getUIColorPalette( isDarkUi );
    const cardStyle = useMemo(
        (): CSSProperties => buildItineraryListCardStyle( palette, isDarkUi ),
        [ isDarkUi, palette ],
    );
    const [ route, setRoute ] = useState<Route | null>( null );
    const [ loading, setLoading ] = useState<boolean>( true );

    const routeHistory = useEphemeralSelector( routeHistorySlice, ( history ) => history );
    const navigationMode = useEphemeralSelector( navigationSessionSlice, ( session ) => session.navigationMode );

    // Navigation services

    // Derived state from NavigationMode
    const isNavigating = navigationMode === NavigationMode.CONSTRAINED_NAVIGATION;

    const [ userLocation, setUserLocation ] = useState( getUserLocation() );
    const [ searchLocation, setSearchLocation ] = useState<{
        "latitude":  number;
        "longitude": number;
    }>( userLocation );
    const inputsRef = useRef( selectedInputs );

    // Subscribe to GPS location updates
    useEffect( () => {
        const unsubscribe = subscribeToLocation( setUserLocation );

        return unsubscribe;
    }, [] );

    useEffect( () => {
        if ( inputsRef.current !== selectedInputs ) {
            setSearchLocation( userLocation );
        }
    }, [ selectedInputs, userLocation ] );

    /**
     * Met à jour la position courante de l'utilisateur
     */
    const userLocationFill = useCallback( ( loc: Location ) => {
        const newLoc = { ...loc };
        if ( loc.lat === -1 && loc.lat === -1 && !isNavigating ) {
            newLoc.lat = searchLocation.latitude;
            newLoc.lon = searchLocation.longitude;
        }

        return newLoc;
    }, [ searchLocation.latitude, searchLocation.longitude, isNavigating ] );

    useEffect( () => {
        if ( isNavigating ) {
            return;
        }
        const getRoutes = async() => {
            const parameters = {
                "source":               isReturn ? userLocationFill( selectedInputs.arrival ) : userLocationFill( selectedInputs.departure ),
                "destination":          isReturn ? userLocationFill( selectedInputs.departure ) : userLocationFill( selectedInputs.arrival ),
                type,
                "timestamp":            Date.now(),
                "isDepartureTimestamp": true,
            };

            const existingRoute = routeHistory.find( ( entry: {
                "parameters": {
                    "source": {
                        "lat": number;
                        "lon": number;
                    };
                    "destination": {
                        "lat": number;
                        "lon": number;
                    };
                    "type": string;
                };
            } ) => entry.parameters.source.lat === parameters.source.lat &&
              entry.parameters.source.lon === parameters.source.lon &&
              entry.parameters.destination.lat === parameters.destination.lat &&
              entry.parameters.destination.lon === parameters.destination.lon &&
              entry.parameters.type === parameters.type );

            if ( existingRoute !== null && existingRoute !== undefined ) {
                setRoute( existingRoute.response );
                if ( isReturn ) {
                    setBackupReturnItineraries( ( lastItineraries ) => {
                        const itineraryExists = lastItineraries.some( ( itinerary ) => itinerary.line === existingRoute.response.line );
                        if ( !itineraryExists ) {
                            return [
                                ...lastItineraries,
                                {
                                    ...existingRoute.response,
                                    "isReturn": !!isReturn,
                                },
                            ];
                        } else {
                            return lastItineraries;
                        }
                    } );
                } else {
                    setGoItineraries( ( lastItineraries ) => {
                        const itineraryExists = lastItineraries.some( ( itinerary ) => itinerary.line === existingRoute.response.line );
                        if ( !itineraryExists ) {
                            return [
                                ...lastItineraries,
                                {
                                    ...existingRoute.response,
                                    "isReturn": !!isReturn,
                                },
                            ];
                        } else {
                            return lastItineraries;
                        }
                    } );
                }
                setLoading( false );
            } else {
                const response = await itineraryService.getCarItinerary( parameters );
                const routeData = response.data as Route | null;
                if ( routeData !== null ) {
                    setRoute( routeData );

                    if ( isReturn ) {
                        setBackupReturnItineraries( ( lastItineraries ) => {
                            const itineraryExists = lastItineraries.some( ( itinerary ) => itinerary.line === routeData.line );
                            if ( !itineraryExists ) {
                                return [
                                    ...lastItineraries,
                                    {
                                        ...routeData,
                                        "isReturn": !!isReturn,
                                    },
                                ];
                            } else {
                                return lastItineraries;
                            }
                        } );
                    } else {
                        setGoItineraries( ( lastItineraries ) => {
                            const itineraryExists = lastItineraries.some( ( itinerary ) => itinerary.line === routeData.line );
                            if ( !itineraryExists ) {
                                return [
                                    ...lastItineraries,
                                    {
                                        ...routeData,
                                        "isReturn": !!isReturn,
                                    },
                                ];
                            } else {
                                return lastItineraries;
                            }
                        } );
                    }


                    const routeHistory: RouteHistory = {
                        parameters,
                        "response":  response.data as Route,
                        "timestamp": Date.now(),
                    };

                    appendRouteHistory( routeHistory );
                }
                setLoading( false );
            }
        };

        void getRoutes();
    }, [ isReturn, selectedInputs.arrival, selectedInputs.departure, setBackupReturnItineraries, setGoItineraries, setItineraries, type, routeHistory, userLocationFill, isNavigating ] );

    if ( loading ) {
        return <></>;
    }

    const isSelected = isReturn
        ? userChoice.returnRoute?.id === route?.id
        : userChoice.route?.id === route?.id;
    const routeTypeAccent = route?.type === "eco"
        ? palette.brandPrimary
        : route?.type === "sp"
            ? palette.overlayTextSecondary
            : palette.myCarPricePillAccent;

    return route !== null
        ? (
        <div className="relative mr-1 min-w-[100px]">
            {route.type === "eco" && <img
                className="absolute -right-2 -top-2 z-10"
                src={LeafIcon}
                                     />}
            <button
                className={`${ ITINERARY_LIST_CARD_CHROME_CLASSNAME } w-full cursor-pointer text-left text-sm transition-transform duration-150 ease-out active:scale-[0.98]`}
                onClick={() => {
                    setSelectedCarRouteId( route.id );
                    updateUserChoice( { [isReturn ? "returnRoute" : "route"]: route } );
                }}
                style={{
                    ...cardStyle,
                    "borderColor": isSelected ? palette.brandPrimary : palette.overlayBorderStrong,
                    "boxShadow":   isSelected
                        ? `0 0 0 1px ${ palette.brandPrimary }, ${ cardStyle.boxShadow ?? "" }`
                        : cardStyle.boxShadow,
                }}
                type="button"
            >
                <div className="flex items-center justify-start">
                    <ClockIcon
                        className="mr-1 inline-block h-4 w-4 shrink-0 align-middle"
                        style={{ "color": palette.overlayTextSecondary }}
                    />
                    <p
                        className="text-xs font-semibold"
                        style={{ "color": palette.overlayTextPrimary }}
                    >
                        {`${ formatTime( route.duration, true ) }`}
                    </p>
                </div>
                <p
                    className="mt-0.5 text-xs font-bold"
                    style={{ "color": routeTypeAccent }}
                >
                    {route.type === "p" && "Péages"}
                    {route.type === "sp" && "Sans Péages"}
                    {route.type === "eco" && "ECO"}
                </p>
                <p
                    className="mt-0.5 text-[11px] font-medium"
                    style={{ "color": palette.overlayTextSecondary }}
                >
                    {`${ route.distance } Km`}
                </p>
            </button>
        </div>
        )
        : (
        <></>
        );
};

export default CarItineraryButton;
